<template>
    <b-form-group :state="state" :invalid-feedback="invalidFeedback">
        <template slot="label">
            <span class="label">{{ label }}</span>
        </template>
        <multiselect
            id="periods"
            :state="state"
            :selected="value"
            :loading="isLoading"
            :disabled="disabled"
            :options="options"
            :multiple="multiple"
            :showSelectLabel="false"
            :showSelectedLabel="true"
            :showDeselectLabel="false"
            :placeholder="placeholder"
            :closeOnSelect="closeOnSelect"
            :hideSelectedOptions="hideSelectedOptions"
            limitTextTranslationPath="form.placeholder.andMorePeriods"
            :numberOfSelectedOptionsToDisplay="numberOfSelectedOptionsToDisplay"
            @click="getPeriods"
            @changeValue="onChangePeriods"
        >
            <template slot="c-option" slot-scope="{ options: { option } }">
                <div class="d-flex justify-content-between">
                    <span>{{ option.label }}</span>
                    <div>
                        <i :class="`${status[option.status].icon} mr-1`" />
                        <span>{{ status[option.status].text }}</span>
                    </div>
                </div>
            </template>
        </multiselect>
    </b-form-group>
</template>

<script>
import i18n from '@/i18n'
import api from '@/services/api'
import { Multiselect, mapperToOptions } from '@/views/components/selects'
import { getActivePeriods } from '@/lib/periods'
import { debounce } from 'lodash'

export default {
    name: 'Periods',
    components: {
        Multiselect,
    },
    props: {
        label: String,
        disabled: Boolean,
        placeholder: String,
        lazyLoading: Boolean,
        notGetActive: Boolean,
        value: [Array, Object],
        invalidFeedback: String,
        applyScopeArchived: Boolean,
        applyScopeNotArchived: Boolean,
        applyDebounce: {
            type: Boolean,
            default: true,
        },
        closeOnSelect: {
            type: Boolean,
            default: true,
        },
        hideSelectedOptions: {
            type: Boolean,
            default: true,
        },
        numberOfSelectedOptionsToDisplay: {
            type: Number,
            default: 99999,
        },
        multiple: {
            type: Boolean,
            default: true,
        },
        state: {
            type: Boolean | null,
            default: null,
        },
    },
    data() {
        return {
            periods: [],
            isLoaded: false,
            isLoading: false,
            debouncedOnChangePeriods: debounce(this.emitChangeEvents, 700),
        }
    },
    computed: {
        status() {
            return {
                finished: {
                    icon: 'fa fa-ban',
                    text: i18n.t('periods.statuses.finished'),
                },
                in_progress: {
                    icon: 'fa fa-calendar-check-o',
                    text: i18n.t('periods.statuses.in_progress'),
                },
                in_draft: {
                    icon: 'fa fa-edit',
                    text: i18n.t('periods.statuses.in_draft'),
                },
                not_started: {
                    icon: null,
                    text: i18n.t('periods.statuses.not_started'),
                },
            }
        },
        options() {
            return mapperToOptions(this.periods, undefined, 'name')
        },
        scopes() {
            const items = []
            if (this.applyScopeArchived) {
                items.push('archived')
            }

            if (this.applyScopeNotArchived) {
                items.push('notArchived')
            }

            return items
        },
    },
    mounted() {
        if (!this.lazyLoading) {
            this.getPeriods()
        }
    },
    methods: {
        getPeriods() {
            if (this.isLoaded) {
                return
            }

            this.isLoading = true
            return api
                .get('/periods', {
                    params: {
                        sortedBy: 'desc',
                        orderBy: 'end_date',
                        scopes: this.scopes.join(),
                    },
                })
                .then((resp) => {
                    this.periods = resp.data.data
                })
                .catch((e) => {
                    console.log(e)
                })
                .finally(() => {
                    this.isLoaded = true
                    this.isLoading = false
                    this.setDefaultSelectedPeriods()
                    this.$emit('ready', true)
                })
        },
        setDefaultSelectedPeriods() {
            if (this.notGetActive) {
                return
            }
            const activedPeriods = getActivePeriods(this.options)
            this.onChangePeriods(activedPeriods)
        },
        onChangePeriods(value) {
            if (this.applyDebounce) {
                this.debouncedOnChangePeriods(value)
            } else {
                this.emitChangeEvents(value)
            }
        },
        emitChangeEvents(value) {
            this.$emit('change')
            this.$emit('update:value', value)
            this.$store.dispatch('applyPeriodsFilter', value)
        },
    },
}
</script>

<style lang="scss" scope>
@import '@/scss/CustomMultiselect.scss';

.c-th:hover {
    cursor: pointer;
}

#custom-multiselect .multiselect {
    border: 1px solid #a3a3a3 !important;
}
</style>
